/**
 * VIN decode hook. Calls the NHTSA vPIC public decoder directly from the
 * browser and exposes a small state machine. Never mutates form state itself —
 * the caller decides what to do with `decoded` on success.
 *
 * State machine: idle -> loading -> (success | notFound | error)
 *
 * @module vehicleModal/hooks/useVinLookup
 */
import { useCallback, useRef, useState } from "react";
import { NHTSA_DECODE_VIN_URL, VIN_LENGTH } from "../constants";
import { hasVehicleData, mapVinResponse } from "../utils/vinMapping";

/**
 * @returns {{
 *   status: import("../types").VinLookupStatus,
 *   decoded: import("../types").VinLookupResponse|null,
 *   error: string|null,
 *   lookup: (vin: string) => Promise<import("../types").VinLookupResponse|null>,
 *   reset: () => void,
 * }}
 */
export default function useVinLookup() {
  const [status, setStatus] = useState("idle");
  const [decoded, setDecoded] = useState(null);
  const [error, setError] = useState(null);
  // Guards against a slow earlier request overwriting a newer one's result.
  const requestIdRef = useRef(0);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setStatus("idle");
    setDecoded(null);
    setError(null);
  }, []);

  const lookup = useCallback(async vin => {
    const trimmed = (vin || "").trim().toUpperCase();
    if (trimmed.length !== VIN_LENGTH) {
      // Caller should gate on length; treat as a no-op reset to idle.
      reset();
      return null;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setStatus("loading");
    setDecoded(null);
    setError(null);

    try {
      const res = await fetch(`${NHTSA_DECODE_VIN_URL}/${encodeURIComponent(trimmed)}?format=json`);
      if (requestId !== requestIdRef.current) return null; // superseded
      if (!res.ok) throw new Error(`VIN service returned ${res.status}`);

      const json = await res.json();
      if (requestId !== requestIdRef.current) return null;

      const mapped = mapVinResponse(json);
      if (hasVehicleData(mapped)) {
        setDecoded(mapped);
        setStatus("success");
        return mapped;
      }
      setStatus("notFound");
      return null;
    } catch (err) {
      if (requestId !== requestIdRef.current) return null;
      setError(err?.message || "VIN lookup failed");
      setStatus("error");
      return null;
    }
    // reset is stable (useCallback []), safe to omit from deps without lint noise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, decoded, error, lookup, reset };
}
